import React, { useEffect, useState } from 'react';
import { AppearancePreview } from './appearance/AppearancePreview';
import { StaticSpritePreview } from './appearance/StaticSpritePreview';
import { fetchCharacterDefinition } from '../../services/characterDefinitionService';
import { useInventoryStore } from '../../stores/inventoryStore';
import { useGameStore } from '../../stores/gameStore';
import { useNpcStore } from '../../stores/npcStore';
import { gameService } from '../../services/gameService';
import { abilityService } from '../../services/abilityService';
import {
  AbilityDTO,
  AttributeEnum,
  CharacterDefinition,
  getCombatMovement,
  getAttributeModifier,
  getArmor,
  getMaxHP,
  getMaxMP,
  IAttributes,
  MotivationDTO,
  NpcPublicDTO,
  PlayerCharacterDTO,
  SkillEnum,
  TargetType,
} from '@hexcrawl/shared-utils';
import {
  ATTRIBUTE_ICONS,
  ATTRIBUTE_LABELS,
  DERIVED_STAT_ICONS,
  DERIVED_STAT_LABELS,
  SKILL_ICONS,
  SKILL_LABELS,
} from '../../constants/entityDisplay';
import { InventoryPanel } from '../inventory/InventoryPanel';

type Tab = 'stats' | 'motivations' | 'inventory' | 'party';

interface Props {
  character: PlayerCharacterDTO;
  onClose: () => void;
}

export const CharacterPanel: React.FC<Props> = ({ character, onClose }) => {
  const [activeTab, setActiveTab] = useState<Tab>('stats');
  const maxHP = getMaxHP(character);
  const maxMP = getMaxMP(character);
  const move = getCombatMovement(character);
  const equipment = useInventoryStore((s) => s.equipment);
  const inventory = useInventoryStore((s) => s.inventory);
  const equippedItems = inventory.filter(i => Object.values(equipment).includes(i.id));
  const armor = getArmor(equippedItems);
  const [definition, setDefinition] = useState<CharacterDefinition | null>(null);

  useEffect(() => {
    fetchCharacterDefinition(character.id)
      .then(setDefinition)
      .catch(e => console.warn('[CharacterPanel] Failed to fetch character definition:', e));
  // equipment reference changes when the player equips/unequips an item, which
  // changes the definition (server applies equipment overrides server-side).
  }, [character.id, equipment]);

  const abilityIds = useInventoryStore((s) => s.abilities);
  const [abilities, setAbilities] = useState<AbilityDTO[]>([]);

  useEffect(() => {
    if (abilityIds.length === 0) {
      setAbilities([]);
      return;
    }
    let cancelled = false;
    Promise.all(abilityIds.map(id => abilityService.getAbility(id)))
      .then(fetched => { if (!cancelled) setAbilities(fetched); })
      .catch(err => console.error('Failed to fetch abilities:', err));
    return () => { cancelled = true; };
  }, [abilityIds]);

  const allNpcs = useNpcStore((s) => s.npcs);
  const removeNpc = useNpcStore((s) => s.removeNpc);
  const gameState = useGameStore((s) => s.gameState);
  const setGameState = useGameStore((s) => s.setGameState);
  const hiredNpcIds = gameState?.hiredNpcIds ?? [];
  const hirelings = hiredNpcIds.map(id => allNpcs[id]).filter(Boolean) as NpcPublicDTO[];
  const [dismissingNpcId, setDismissingNpcId] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'party' && hirelings.length === 0) {
      setActiveTab('stats');
    }
  }, [hirelings.length, activeTab]);

  const handleDismissHireling = async (npcId: string) => {
    if (!gameState || dismissingNpcId) return;
    setDismissingNpcId(npcId);
    try {
      const result = await gameService.dismissHireling(gameState.id, npcId);
      removeNpc(npcId);
      setGameState({ ...gameState, hiredNpcIds: result.hiredNpcIds });
    } catch (err) {
      console.error('Failed to dismiss hireling:', err);
    } finally {
      setDismissingNpcId(null);
    }
  };

  return (
    <div className="char-panel-overlay" onClick={onClose}>
      <div className="char-panel" onClick={(e) => e.stopPropagation()}>
        <button className="char-panel-close" onClick={onClose}>✕</button>

        {/* Header — always visible */}
        <div className="char-panel-header">
          {character.appearance && (
            <AppearancePreview
              definition={definition}
              size={80}
              loading={!definition}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20, fontWeight: 'bold', color: '#e8e8ff', fontFamily: 'monospace' }}>
                {character.name}
              </span>
              <span style={{ fontSize: 12, color: '#888', fontFamily: 'monospace' }}>Level {character.level}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <DerivedStat icon={DERIVED_STAT_ICONS.maxHp} label={DERIVED_STAT_LABELS.hp} value={`${character.currentHP}/${maxHP}`} color="#e88" />
              <DerivedStat icon={DERIVED_STAT_ICONS.maxMp} label={DERIVED_STAT_LABELS.mp} value={`${character.currentMP}/${maxMP}`} color="#88c" />
              <DerivedStat icon={DERIVED_STAT_ICONS.combatMove} label={DERIVED_STAT_LABELS.combatMove} value={String(move)} color="#8c8" />
              <DerivedStat icon={DERIVED_STAT_ICONS.armor} label={DERIVED_STAT_LABELS.armor} value={String(armor)} color="#aac" />
            </div>
          </div>
        </div>

        {/* Tab bar — only visible on small screens via CSS */}
        <div className="char-panel-tabbar">
          <button
            className={`char-panel-tab${activeTab === 'stats' ? ' active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Stats
          </button>
          <button
            className={`char-panel-tab${activeTab === 'motivations' ? ' active' : ''}`}
            onClick={() => setActiveTab('motivations')}
          >
            Motivations
          </button>
          <button
            className={`char-panel-tab${activeTab === 'inventory' ? ' active' : ''}`}
            onClick={() => setActiveTab('inventory')}
          >
            Inventory
          </button>
          {hirelings.length > 0 && (
            <button
              className={`char-panel-tab${activeTab === 'party' ? ' active' : ''}`}
              onClick={() => setActiveTab('party')}
            >
              Party ({hirelings.length})
            </button>
          )}
        </div>

        {/* Content */}
        <div className="char-panel-content">
          {/* Stats section */}
          <div className={`char-panel-section char-panel-stats-col${activeTab !== 'stats' ? ' char-panel-section--hidden-mobile' : ''}`}>
            <div className="char-panel-attrs-skills">
              <div style={{ flex: 1 }}>
                <SectionHeader>Attributes</SectionHeader>
                {Object.values(AttributeEnum).map((key) => {
                  const val = character.attributes[key] ?? 0;
                  const mod = getAttributeModifier(val);
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{ATTRIBUTE_ICONS[key]}</span>
                      <span style={{ flex: 1, fontSize: 13, fontFamily: 'monospace', color: '#ccc' }}>{ATTRIBUTE_LABELS[key]}</span>
                      <span style={{ fontSize: 14, fontWeight: 'bold', color: '#e8e8ff', minWidth: 22, textAlign: 'right', fontFamily: 'monospace' }}>{val}</span>
                      <span style={{
                        fontSize: 11, minWidth: 28, textAlign: 'right', fontFamily: 'monospace',
                        color: mod > 0 ? '#88cc88' : mod < 0 ? '#cc8888' : '#666',
                      }}>
                        {mod >= 0 ? `+${mod}` : `${mod}`}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="char-panel-col-divider" />

              <div style={{ flex: 1 }}>
                <SectionHeader>Skills</SectionHeader>
                {Object.values(SkillEnum).map((key) => {
                  const val = character.skills[key] ?? 0;
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{SKILL_ICONS[key]}</span>
                      <span style={{ flex: 1, fontSize: 13, fontFamily: 'monospace', color: '#ccc' }}>{SKILL_LABELS[key]}</span>
                      <span style={{
                        fontSize: 14, fontWeight: 'bold', minWidth: 22, textAlign: 'right', fontFamily: 'monospace',
                        color: val > 0 ? '#e8e8ff' : '#444',
                      }}>{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {abilities.length > 0 && (
              <>
                <div style={{ height: 1, background: '#223', margin: '12px 0 10px' }} />
                <SectionHeader>Abilities</SectionHeader>
                {abilities.map((ability) => (
                  <AbilityRow
                    key={ability.id}
                    ability={ability}
                    characterAttributes={character.attributes as IAttributes}
                    characterSkills={character.skills as Record<string, number | undefined>}
                  />
                ))}
              </>
            )}
          </div>

          <div className="char-panel-divider" />

          {/* Motivations section */}
          <div className={`char-panel-section${activeTab !== 'motivations' ? ' char-panel-section--hidden-mobile' : ''}`}>
            <SectionHeader>Motivations</SectionHeader>
            {character.motivations.filter((m) => m.answer?.trim()).length > 0 ? (
              character.motivations
                .filter((m) => m.answer?.trim())
                .map((m, idx) => <MotivationRow key={idx} motivation={m} />)
            ) : (
              <div style={{ fontSize: 12, color: '#667788', fontFamily: 'monospace', fontStyle: 'italic' }}>
                No motivations defined.
              </div>
            )}
          </div>

          <div className="char-panel-divider" />

          {/* Inventory section */}
          <div className={`char-panel-section char-panel-inv-col${activeTab !== 'inventory' ? ' char-panel-section--hidden-mobile' : ''}`}>
            <InventoryPanel showGrantCheat />
          </div>

          {hirelings.length > 0 && (
            <>
              <div className="char-panel-divider" />
              <div className={`char-panel-section char-panel-party-col${activeTab !== 'party' ? ' char-panel-section--hidden-mobile' : ''}`}>
                <SectionHeader>Party</SectionHeader>
                {hirelings.map((npc) => (
                  <PartyHirelingRow
                    key={npc.id}
                    npc={npc}
                    dismissing={dismissingNpcId === npc.id}
                    onDismiss={() => handleDismissHireling(npc.id)}
                  />
                ))}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 'bold', letterSpacing: 1.5, color: '#556',
    textTransform: 'uppercase', marginBottom: 8, borderBottom: '1px solid #223',
    paddingBottom: 4, fontFamily: 'monospace',
  }}>
    {children}
  </div>
);

const MotivationRow: React.FC<{ motivation: MotivationDTO }> = ({ motivation }) => (
  <div style={{
    padding: '8px 10px',
    marginBottom: 8,
    background: 'rgba(22, 53, 74, 0.5)',
    border: '1px solid #2a4a6a',
    borderRadius: 6,
  }}>
    <div style={{ fontSize: 12, color: '#88a0b8', fontFamily: 'monospace', marginBottom: 4 }}>
      {motivation.question}
    </div>
    <div style={{ fontSize: 13, color: '#d9ecff', fontFamily: 'monospace', lineHeight: 1.4 }}>
      {motivation.answer}
    </div>
  </div>
);

const AbilityRow: React.FC<{
  ability: AbilityDTO;
  characterAttributes: IAttributes;
  characterSkills: Record<string, number | undefined>;
}> = ({ ability, characterAttributes, characterSkills }) => {
  const args = ability.script?.args ?? {};
  const minDmgBase = args.minDamage != null ? Number(args.minDamage) : null;
  const maxDmgBase = args.maxDamage != null ? Number(args.maxDamage) : null;
  const casterSkill = args.casterSkill ? String(args.casterSkill) : null;
  const skillRank = casterSkill != null ? (characterSkills[casterSkill] ?? 0) : null;
  const damageModKey = args.damageMod ? String(args.damageMod) as keyof IAttributes : null;
  const damageMod = damageModKey ? getAttributeModifier(characterAttributes[damageModKey] ?? 10) : 0;
  const minDmg = minDmgBase != null ? Math.max(1, minDmgBase + damageMod) : null;
  const maxDmg = maxDmgBase != null ? Math.max(1, maxDmgBase + damageMod) : null;

  return (
    <div style={{
      padding: '6px 8px',
      marginBottom: 6,
      background: 'rgba(22, 53, 74, 0.5)',
      border: '1px solid #2a4a6a',
      borderRadius: 6,
    }}>
      <div style={{ fontWeight: 'bold', fontSize: 13, fontFamily: 'monospace', color: '#d9ecff' }}>{ability.name}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 3 }}>
        {minDmg != null && maxDmg != null && (
          <span style={{ fontSize: 11, color: '#ff9966', fontFamily: 'monospace' }}>{minDmg}–{maxDmg} dmg</span>
        )}
        {skillRank != null && casterSkill != null && (
          <span style={{ fontSize: 11, color: '#88c0ff', fontFamily: 'monospace' }}>+{skillRank} {casterSkill}</span>
        )}
        {ability.mpCost > 0 && (
          <span style={{ fontSize: 11, color: '#88aaff', fontFamily: 'monospace' }}>{ability.mpCost} MP</span>
        )}
        <span style={{ fontSize: 11, color: '#667788', fontFamily: 'monospace', marginLeft: 'auto' }}>
          Targets: {targetLabel(ability.target.type)}
        </span>
      </div>
    </div>
  );
};

function targetLabel(type: TargetType): string {
  const parts: string[] = [];
  if (type & TargetType.SELF) parts.push('Self');
  if (type & TargetType.ALLIES) parts.push('Allies');
  if (type & TargetType.ENEMIES) parts.push('Enemies');
  return parts.join('/');
}

const DerivedStat: React.FC<{ icon: string; label: string; value: string; color: string }> = ({ icon, label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{icon}</span>
    <span style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>{label}</span>
    <span style={{ fontSize: 14, fontWeight: 'bold', color, fontFamily: 'monospace' }}>{value}</span>
  </div>
);

interface PartyHirelingRowProps {
  npc: NpcPublicDTO;
  dismissing: boolean;
  onDismiss: () => void;
}

const PartyHirelingRow: React.FC<PartyHirelingRowProps> = ({ npc, dismissing, onDismiss }) => {
  const maxHP = getMaxHP(npc);
  const maxMP = getMaxMP(npc);
  const hpPct = maxHP > 0 ? Math.max(0, Math.min(1, npc.currentHP / maxHP)) : 0;
  const mpPct = maxMP > 0 ? Math.max(0, Math.min(1, npc.currentMP / maxMP)) : 0;

  const sprite = (() => {
    if (npc.spriteType === 'composite' && npc.characterDefinition) {
      return <AppearancePreview definition={npc.characterDefinition} size={48} />;
    }
    if (npc.spriteType === 'static' && npc.sprite) {
      return <StaticSpritePreview filename={npc.sprite} size={48} />;
    }
    return null;
  })();

  return (
    <div style={{
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid #1a3a5a',
      borderRadius: 8,
      padding: '8px 10px',
      marginBottom: 8,
    }}>
      {sprite && <div style={{ flexShrink: 0 }}>{sprite}</div>}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ color: '#d0d0d0', fontSize: 13, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {npc.name}
          </span>
          <span style={{ color: '#7799bb', fontSize: 11, flexShrink: 0, marginLeft: 6 }}>
            Lv {npc.level}
          </span>
        </div>
        <HirelingVitalBar value={npc.currentHP} max={maxHP} pct={hpPct} label="HP" color="#44cc66" />
        <HirelingVitalBar value={npc.currentMP} max={maxMP} pct={mpPct} label="MP" color="#4488ff" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
        <button
          onClick={onDismiss}
          disabled={dismissing}
          title="Dismiss"
          style={{
            background: 'none',
            border: '1px solid #5a3030',
            borderRadius: 4,
            color: dismissing ? '#4a3a3a' : '#cc5555',
            fontFamily: 'monospace',
            fontSize: 11,
            padding: '3px 7px',
            cursor: dismissing ? 'wait' : 'pointer',
          }}
        >
          {dismissing ? '…' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

interface HirelingVitalBarProps {
  value: number;
  max: number;
  pct: number;
  label: string;
  color: string;
}

const HirelingVitalBar: React.FC<HirelingVitalBarProps> = ({ value, max, pct, label, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ color: '#6688aa', fontSize: 10, width: 14 }}>{label}</span>
    <div style={{ flex: 1, height: 5, background: '#0a1a2a', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{
        width: `${pct * 100}%`,
        height: '100%',
        background: color,
        borderRadius: 3,
        transition: 'width 0.2s ease',
      }} />
    </div>
    <span style={{ color: '#7799bb', fontSize: 10, width: 32, textAlign: 'right' }}>{value}/{max}</span>
  </div>
);
